import React, { useState, useEffect } from 'react';
import regimenLaboralService from '../services/regimenLaboralService';

const RegimenesLaborales = () => {
  const [regimenes, setRegimenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRegimen, setEditingRegimen] = useState(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Cargar regímenes al montar el componente
  useEffect(() => {
    loadRegimenes();
  }, []);

  // Cargar todos los regímenes laborales
  const loadRegimenes = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await regimenLaboralService.getAll();
      
      if (result.success) {
        setRegimenes(result.data);
        setSuccess('Regímenes laborales cargados exitosamente');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Error al cargar los regímenes laborales');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Buscar regímenes
  const handleSearch = async (query) => {
    if (!query.trim()) {
      loadRegimenes();
      return;
    }

    setLoading(true);
    try {
      const result = await regimenLaboralService.search(query);
      
      if (result.success) {
        setRegimenes(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Error en la búsqueda');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error específico cuando el usuario empiece a escribir
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    const validation = regimenLaboralService.validateRegimenData(formData);
    setFormErrors(validation.errors);
    return validation.isValid;
  };

  // Abrir modal para crear nuevo régimen
  const handleCreate = () => {
    setEditingRegimen(null);
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Abrir modal para editar régimen
  const handleEdit = (regimen) => {
    setEditingRegimen(regimen);
    setFormData({
      codigo: regimen.codigo,
      nombre: regimen.nombre,
      descripcion: regimen.descripcion || ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Guardar régimen (crear o actualizar)
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      let result;
      
      if (editingRegimen) {
        result = await regimenLaboralService.update(editingRegimen.id, formData);
      } else {
        result = await regimenLaboralService.create(formData);
      }

      if (result.success) {
        setSuccess(result.message);
        setShowModal(false);
        loadRegimenes();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message);
        if (result.errors) {
          setFormErrors(result.errors);
        }
      }
    } catch (err) {
      setError('Error al guardar el régimen laboral');
    } finally {
      setLoading(false);
    }
  };

  // Eliminar régimen
  const handleDelete = async (regimen) => {
    if (!window.confirm(`¿Está seguro de eliminar el régimen "${regimen.nombre}"?`)) {
      return;
    }

    setLoading(true);
    try {
      const result = await regimenLaboralService.delete(regimen.id);
      
      if (result.success) {
        setSuccess(result.message);
        loadRegimenes();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Error al eliminar el régimen laboral');
    } finally {
      setLoading(false);
    }
  };

  // Cerrar modal
  const closeModal = () => {
    setShowModal(false);
    setEditingRegimen(null);
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: ''
    });
    setFormErrors({});
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Gestión de Regímenes Laborales</h3>
          <p className="mt-1 text-sm text-gray-500">
            Configura los diferentes regímenes laborales y sus beneficios
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Define los derechos y beneficios laborales según cada régimen aplicable en Perú
          </p>
        </div>
        <button
          onClick={handleCreate}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nuevo Régimen
        </button>
      </div>

      {/* Mensajes */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Buscador */}
      <div className="max-w-md">
        <label htmlFor="search" className="sr-only">Buscar regímenes</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            id="search"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Buscar por código, nombre o descripción..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              handleSearch(e.target.value);
            }}
          />
        </div>
      </div>

      {/* Tabla de regímenes */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-2 text-gray-600">Cargando regímenes...</span>
            </div>
          ) : regimenes.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No hay regímenes laborales</h3>
              <p className="mt-1 text-sm text-gray-500">Comienza creando un nuevo régimen laboral.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Régimen
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {regimenes.map((regimen) => (
                    <tr key={regimen.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {regimen.codigo}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{regimen.nombre}</div>
                        <div className="text-sm text-gray-500">
                          {regimen.descripcion && regimen.descripcion.length > 100 
                            ? regimen.descripcion.substring(0, 100) + '...' 
                            : regimen.descripcion || 'Sin descripción'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Activo
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(regimen)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(regimen)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Desactivar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar régimen */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingRegimen ? 'Editar Régimen Laboral' : 'Nuevo Régimen Laboral'}
              </h3>
              
              <div className="space-y-4">
                {/* Código */}
                <div>
                  <label htmlFor="codigo" className="block text-sm font-medium text-gray-700">
                    Código *
                  </label>
                  <input
                    type="text"
                    id="codigo"
                    name="codigo"
                    value={formData.codigo}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                      formErrors.codigo ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ej: RG, MYPE, AGRARIO"
                    maxLength={20}
                  />
                  {formErrors.codigo && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.codigo}</p>
                  )}
                </div>

                {/* Nombre */}
                <div>
                  <label htmlFor="nombre" className="block text-sm font-medium text-gray-700">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                      formErrors.nombre ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Nombre del régimen laboral"
                    maxLength={200}
                  />
                  {formErrors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.nombre}</p>
                  )}
                </div>

                {/* Descripción */}
                <div>
                  <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700">
                    Descripción
                  </label>
                  <textarea
                    id="descripcion"
                    name="descripcion"
                    rows={3}
                    value={formData.descripcion}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                      formErrors.descripcion ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Descripción del régimen laboral"
                    maxLength={1000}
                  />
                  {formErrors.descripcion && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.descripcion}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegimenesLaborales; 